import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Info, Key } from 'lucide-react';
import type { ApiLNSServer, CreateLNSServerRequest, UpdateLNSServerRequest } from '@/types/api';

const lnsServerSchema = z.object({
  name: z.string().min(1, 'El nombre es requerido').max(100),
  type: z.enum(['chirpstack_v3', 'chirpstack_v4']),
  serverUrl: z.string().url('URL inválida').min(1, 'La URL es requerida'),
  apiKey: z.string().optional(),
  controllerApplicationId: z.string().optional(),
  sensorApplicationId: z.string().optional(),
});

type LNSServerFormData = z.infer<typeof lnsServerSchema>;

interface LNSServerFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  server?: ApiLNSServer | null;
  onSubmit: (data: CreateLNSServerRequest | UpdateLNSServerRequest) => void;
  isLoading?: boolean;
}

export function LNSServerFormDialog({
  open,
  onOpenChange,
  server,
  onSubmit,
  isLoading = false,
}: LNSServerFormDialogProps) {
  const isEditing = !!server;

  const form = useForm<LNSServerFormData>({
    resolver: zodResolver(lnsServerSchema),
    defaultValues: {
      name: '',
      type: 'chirpstack_v4',
      serverUrl: '',
      apiKey: '',
      controllerApplicationId: '',
      sensorApplicationId: '',
    },
  });

  // Reset form when server changes or dialog opens
  useEffect(() => {
    if (open) {
      if (server) {
        form.reset({
          name: server.name,
          type: server.type,
          serverUrl: server.serverUrl,
          apiKey: '', // Never pre-fill API key for security
          controllerApplicationId: server.controllerApplicationId || '',
          sensorApplicationId: server.sensorApplicationId || '',
        });
      } else {
        form.reset({
          name: '',
          type: 'chirpstack_v4',
          serverUrl: '',
          apiKey: '',
          controllerApplicationId: '',
          sensorApplicationId: '',
        });
      }
    }
  }, [open, server, form]);

  const handleSubmit = (data: LNSServerFormData) => {
    // Build the request based on whether we're creating or editing
    if (isEditing) {
      const updateData: UpdateLNSServerRequest = {
        name: data.name,
        type: data.type,
        serverUrl: data.serverUrl,
        controllerApplicationId: data.controllerApplicationId || undefined,
        sensorApplicationId: data.sensorApplicationId || undefined,
      };
      // Only include apiKey if it was provided (not empty)
      if (data.apiKey && data.apiKey.length > 0) {
        updateData.apiKey = data.apiKey;
      }
      onSubmit(updateData);
    } else {
      // Creating - apiKey is required
      if (!data.apiKey) {
        form.setError('apiKey', { message: 'La API Key es requerida para crear un servidor' });
        return;
      }
      const createData: CreateLNSServerRequest = {
        name: data.name,
        type: data.type,
        serverUrl: data.serverUrl,
        apiKey: data.apiKey,
        controllerApplicationId: data.controllerApplicationId || undefined,
        sensorApplicationId: data.sensorApplicationId || undefined,
      };
      onSubmit(createData);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <DialogTitle className="font-display">
            {isEditing ? 'Editar Servidor LNS' : 'Nuevo Servidor LNS'}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Modifica la configuración del servidor LoRaWAN Network Server (ChirpStack).'
              : 'Añade un nuevo servidor ChirpStack para la comunicación LoRaWAN con dispositivos.'}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nombre *</FormLabel>
                  <FormControl>
                    <Input placeholder="SWAT LNS" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tipo de LNS *</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar tipo" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="chirpstack_v4">ChirpStack V4</SelectItem>
                      <SelectItem value="chirpstack_v3">ChirpStack V3</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="serverUrl"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>URL del Servidor *</FormLabel>
                  <FormControl>
                    <Input placeholder="http://188.245.213.181:8080" {...field} />
                  </FormControl>
                  <FormDescription>
                    URL completa del servidor ChirpStack (incluyendo puerto)
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="apiKey"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    API Key {isEditing ? '(dejar vacío para mantener)' : '*'}
                  </FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        type="password"
                        placeholder={isEditing ? '••••••••••••••••' : 'eyJ0eXAiOiJKV1QiLC...'}
                        className="font-mono pl-10"
                        {...field}
                      />
                    </div>
                  </FormControl>
                  {isEditing && server?.hasApiKey && (
                    <FormDescription className="flex items-center gap-1 text-success">
                      <span className="w-2 h-2 rounded-full bg-success" />
                      API Key configurada
                    </FormDescription>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="controllerApplicationId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Application ID (Controllers)</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder="a5b6a90c-2534-442a-979d-..." 
                        className="font-mono text-sm"
                        {...field} 
                      />
                    </FormControl>
                    <FormDescription className="text-xs">
                      UUID de la aplicación para controladoras
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="sensorApplicationId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Application ID (Sensors)</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder="f42dd323-3188-4c5e-9d83-..." 
                        className="font-mono text-sm"
                        {...field} 
                      />
                    </FormControl>
                    <FormDescription className="text-xs">
                      UUID de la aplicación para sensores
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <Alert className="bg-muted/50">
              <Info className="h-4 w-4" />
              <AlertDescription className="text-sm">
                Los dispositivos se registran automáticamente en ChirpStack al crearlos.
                Usa Application IDs diferentes para separar controladores y sensores.
              </AlertDescription>
            </Alert>

            <DialogFooter className="pt-4">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
              <Button 
                type="submit" 
                className="gradient-primary text-white"
                disabled={isLoading}
              >
                {isLoading ? 'Guardando...' : isEditing ? 'Guardar cambios' : 'Crear Servidor'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
